import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldAlert, Plus } from "lucide-react";

const SEVERITY = {
  minor: "bg-muted text-muted-foreground",
  moderate: "bg-secondary/15 text-secondary",
  major: "bg-destructive/15 text-destructive",
};

const STATUS = {
  open: "bg-[hsl(32_87%_62%/0.15)] text-[hsl(32_60%_32%)]",
  warned: "bg-secondary/15 text-secondary",
  fined: "bg-destructive/15 text-destructive",
  resolved: "bg-primary/10 text-primary",
};

export default function Violations() {
  const { user } = useAuth();
  const isBoard = user?.role === "admin" || user?.role === "board";
  const [items, setItems] = useState([]);
  const [owners, setOwners] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ owner_id: "", title: "", description: "", severity: "minor" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/violations");
    setItems(data);
    if (isBoard) {
      const { data: users } = await api.get("/users");
      setOwners(users.filter((u) => u.role === "owner"));
    }
  }, [isBoard]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/violations", form);
      toast.success("Violation issued");
      setForm({ owner_id: "", title: "", description: "", severity: "minor" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not issue");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    await api.patch(`/violations/${id}`, { status });
    toast.success(`Marked ${status}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="violations-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-secondary text-sm font-medium mb-2">
            <ShieldAlert className="w-4 h-4" /> Compliance
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Violations</h1>
        </div>
        {isBoard && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full h-11" data-testid="new-violation-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Issue violation
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Issue a violation</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="violation-form">
                <div>
                  <Label>Owner</Label>
                  <Select value={form.owner_id} onValueChange={(v) => setForm({ ...form, owner_id: v })}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="viol-owner-select"><SelectValue placeholder="Select owner" /></SelectTrigger>
                    <SelectContent>
                      {owners.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {o.name}{o.unit_number ? ` · ${o.unit_number}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Title</Label>
                  <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 h-11" data-testid="viol-title-input" />
                </div>
                <div>
                  <Label>Details</Label>
                  <Textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 min-h-[100px]" data-testid="viol-desc-input" />
                </div>
                <div>
                  <Label>Severity</Label>
                  <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="viol-severity-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minor">Minor</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="major">Major</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving || !form.owner_id} className="rounded-full" data-testid="viol-submit-btn">
                    {saving ? "Issuing..." : "Issue violation"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-3">
        {items.length === 0 && (
          <div className="hoa-card p-10 text-center text-muted-foreground" data-testid="viol-empty">
            No violations recorded.
          </div>
        )}
        {items.map((v) => (
          <div key={v.id} className="hoa-card p-6" data-testid={`viol-${v.id}`}>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS[v.status]}`}>{v.status}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SEVERITY[v.severity]}`}>{v.severity}</span>
                  <span className="text-xs text-muted-foreground">{v.owner_name}{v.owner_unit ? ` · ${v.owner_unit}` : ""}</span>
                </div>
                <h3 className="font-semibold text-foreground">{v.title}</h3>
                <p className="text-sm text-foreground/70 mt-1 leading-relaxed">{v.description}</p>
                <p className="text-xs text-muted-foreground mt-2">Issued by {v.issued_by} · {new Date(v.created_at).toLocaleString()}</p>
              </div>
              {isBoard && v.status !== "resolved" && (
                <div className="flex flex-wrap gap-2">
                  {v.status === "open" && <Button size="sm" variant="outline" onClick={() => setStatus(v.id, "warned")} data-testid={`viol-warn-${v.id}`}>Warn</Button>}
                  {v.status !== "fined" && <Button size="sm" variant="outline" onClick={() => setStatus(v.id, "fined")} data-testid={`viol-fine-${v.id}`}>Escalate to fine</Button>}
                  <Button size="sm" onClick={() => setStatus(v.id, "resolved")} data-testid={`viol-resolve-${v.id}`}>Resolve</Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
